import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TopBar } from '../components/TopBar';
import { LeftPanel } from '../components/LeftPanel';
import { FleetMap } from '../components/FleetMap';
import { DriverDetail } from '../components/DriverDetail';
import { DeliveryTimeline } from '../components/DeliveryTimeline';
import { DemoControls } from '../components/DemoControls';
import { DesignNotes } from '../components/DesignNotes';
import { ToastNotifications } from '../components/ToastNotifications';
import { SuccessToasts, SuccessToastData } from '../components/ToastNotifications';
import { CallCard } from '../components/CallCard';
import { mockDrivers, mockAlerts, mockDeliveries, mockTriageItems } from '../data/mockData';
import { LogEntry } from '../components/LogsPanel';
import { AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function Dashboard() {
  const [demoState, setDemoState] = useState<'normal' | 'hos' | 'offline' | 'bridge'>('normal');
  const [selectedDriverId, setSelectedDriverId] = useState<string | null>(null);
  const [selectedAlertId, setSelectedAlertId] = useState<string | null>(null);
  const [highlightedDriverIds, setHighlightedDriverIds] = useState<string[] | null>(null);
  const [showHidden, setShowHidden] = useState(false);
  const [toastHoveredDriverIds, setToastHoveredDriverIds] = useState<string[] | null>(null);
  const [alertHoveredDriverIds, setAlertHoveredDriverIds] = useState<string[] | null>(null);
  const [callingDriverId, setCallingDriverId] = useState<string | null>(null);
  const [resolvedAlertIds, setResolvedAlertIds] = useState<string[]>([]);
  const [dismissedAlertIds, setDismissedAlertIds] = useState<string[]>([]);
  const [isRerouting, setIsRerouting] = useState(false);
  const [hoveredRouteId, setHoveredRouteId] = useState<number | null>(null);
  const [successToasts, setSuccessToasts] = useState<SuccessToastData[]>([]);
  const [routeOverrides, setRouteOverrides] = useState<Record<string, import('../data/mockData').RouteStop[]>>({});
  const [statusOverrides, setStatusOverrides] = useState<Record<string, import('../data/mockData').Severity>>({});
  const [logEntries, setLogEntries] = useState<LogEntry[]>([]);
  const addLogEntry = useCallback((entry: Omit<LogEntry, 'id' | 'timestamp'>) => {
    setLogEntries(prev => [...prev, {
      ...entry,
      id: `log-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      timestamp: Date.now()
    }]);
  }, []);
  // Live preview: just update route on map, no side effects
  const handlePreviewRoute = (driverId: string, stops: import('../data/mockData').RouteStop[]) => {
    setRouteOverrides(prev => ({
      ...prev,
      [driverId]: stops
    }));
    // Also preview status change: if stops were removed, show green status
    const driver = mockDrivers.find(d => d.id === driverId);
    if (driver) {
      const originalPending = driver.route.filter(s => s.status !== 'completed').length;
      const newPending = stops.filter(s => s.status !== 'completed').length;
      if (newPending < originalPending) {
        setStatusOverrides(prev => ({
          ...prev,
          [driverId]: 'normal'
        }));
      } else {
        // Revert status preview if stops were re-added
        setStatusOverrides(prev => {
          const next = {
            ...prev
          };
          delete next[driverId];
          return next;
        });
      }
    }
  };
  // Cancel preview: revert route override
  const handleCancelRoutePreview = (driverId: string) => {
    setRouteOverrides(prev => {
      const next = {
        ...prev
      };
      delete next[driverId];
      return next;
    });
    // Also revert any status override that was set during preview
    setStatusOverrides(prev => {
      const next = {
        ...prev
      };
      delete next[driverId];
      return next;
    });
  };
  // Commit: apply route + resolve alerts + update status + show toast
  const handleUpdateRoute = (driverId: string, stops: import('../data/mockData').RouteStop[]) => {
    const driver = mockDrivers.find(d => d.id === driverId);
    const originalPending = driver?.route.filter(s => s.status !== 'completed').length ?? 0;
    const newPending = stops.filter(s => s.status !== 'completed').length;
    setRouteOverrides(prev => ({
      ...prev,
      [driverId]: stops
    }));
    // If stops were removed, resolve alerts and update status
    if (newPending < originalPending && driver) {
      const driverAlertIds = mockAlerts.filter(a => a.driverId === driverId || a.affectedDriverIds?.includes(driverId)).map(a => a.id);
      if (driverAlertIds.length > 0) {
        setResolvedAlertIds(prev => [...prev, ...driverAlertIds.filter(id => !prev.includes(id))]);
      }
      setStatusOverrides(prev => ({
        ...prev,
        [driverId]: 'normal'
      }));
    }
    // Always show success toast on apply
    if (driver) {
      const now = Date.now();
      const newToasts: import('../components/ToastNotifications').SuccessToastData[] = [{
        id: `edit-stops-${now}`,
        message: `${driver.truckId} stops updated`,
        detail: 'Change has been logged and sent to driver.'
      }];
      // Check if any removed stops had associated cargo (undelivered items)
      const originalStopIds = new Set(driver.route.map(s => s.id));
      const newStopIds = new Set(stops.map(s => s.id));
      const removedStopIds = [...originalStopIds].filter(id => !newStopIds.has(id));
      const undeliveredCargo = driver.cargo.filter(c => removedStopIds.includes(c.stopId) && c.status !== 'delivered');
      if (undeliveredCargo.length > 0) {
        const itemCount = undeliveredCargo.length;
        const itemNames = undeliveredCargo.map(c => c.commodity).join(', ');
        newToasts.push({
          id: `undelivered-${now}`,
          message: `${itemCount} item${itemCount > 1 ? 's' : ''} moved to undelivered`,
          detail: itemNames,
          variant: 'warning'
        });
        addLogEntry({
          type: 'warning',
          message: `${itemCount} item${itemCount > 1 ? 's' : ''} moved to undelivered`,
          detail: itemNames,
          truckId: driver.truckId
        });
      }
      setSuccessToasts(prev => [...prev, ...newToasts]);
      addLogEntry({
        type: 'route-update',
        message: `Stops updated for ${driver.truckId}`,
        detail: `${stops.filter(s => s.status !== 'completed').length} pending stops. ${removedStopIds.length > 0 ? `${removedStopIds.length} stop${removedStopIds.length > 1 ? 's' : ''} removed.` : 'Stops reordered.'}`,
        truckId: driver.truckId
      });
    }
  };
  const handleApplyReroute = (driverId: string, suggestion: {
    etaImpactMin: number;
    label: string;
  }) => {
    const driver = mockDrivers.find(d => d.id === driverId);
    if (!driver) return;
    // Compute updated stops with adjusted ETAs
    const updatedStops = driver.route.map(stop => {
      if (stop.status === 'completed') return stop;
      // Parse current ETA and apply the time impact
      const etaClean = stop.eta.replace('~', '');
      const [h, m] = etaClean.split(':').map(Number);
      const totalMin = h * 60 + m + suggestion.etaImpactMin;
      const newH = Math.floor(totalMin / 60) % 24;
      const newM = totalMin % 60;
      const newEta = `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`;
      // If we saved time, at-risk stops may become on-time (pending)
      let newStatus = stop.status;
      if (suggestion.etaImpactMin < 0 && stop.status === 'at-risk') {
        newStatus = 'pending';
      }
      return {
        ...stop,
        eta: newEta,
        status: newStatus
      };
    });
    setRouteOverrides(prev => ({
      ...prev,
      [driverId]: updatedStops
    }));
    // If the reroute saves time, mark driver as normal
    if (suggestion.etaImpactMin < 0) {
      setStatusOverrides(prev => ({
        ...prev,
        [driverId]: 'normal'
      }));
    }
    setIsRerouting(false);
    setHoveredRouteId(null);
    // Show success toast
    setSuccessToasts(prev => [...prev, {
      id: `reroute-${Date.now()}`,
      message: `${driver.truckId} route updated to ${suggestion.label}`,
      detail: 'Change has been logged and sent to driver.'
    }]);
    addLogEntry({
      type: 'reroute',
      message: `Rerouted to ${suggestion.label}`,
      detail: `ETA impact: ${suggestion.etaImpactMin > 0 ? '+' : ''}${suggestion.etaImpactMin} min`,
      truckId: driver.truckId
    });
  };
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'h' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const tag = (e.target as HTMLElement)?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA') return;
        setShowHidden(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  // Handle demo state changes
  useEffect(() => {
    if (demoState === 'hos') {
      setSelectedDriverId('d-marcus');
      setSelectedAlertId('a-hos-marcus');
    } else if (demoState === 'offline') {
      setSelectedDriverId('d-sarah');
      setSelectedAlertId('a-off-sarah');
    } else if (demoState === 'bridge') {
      setSelectedDriverId('d-david');
      setSelectedAlertId('a-brg-david');
    } else {
      setSelectedDriverId(null);
      setSelectedAlertId(null);
      setHighlightedDriverIds(null);
    }
  }, [demoState]);
  const handleSelectAlert = (alertId: string, driverId: string) => {
    setSelectedAlertId(alertId);
    const alert = mockAlerts.find(a => a.id === alertId);
    if (alert?.affectedDriverIds && alert.affectedDriverIds.length > 0) {
      setHighlightedDriverIds(alert.affectedDriverIds);
      setSelectedDriverId(null);
    } else {
      setHighlightedDriverIds(null);
      setSelectedDriverId(driverId);
    }
  };
  const [scrollToRouteTs, setScrollToRouteTs] = useState<number | null>(null);
  const [selectedDeliveryRef, setSelectedDeliveryRef] = useState<string | null>(null);
  const handleSelectDriver = (driverId: string) => {
    setSelectedDriverId(driverId);
    setHighlightedDriverIds(null);
    setScrollToRouteTs(null);
    setSelectedDeliveryRef(null);
    // Find if there's an active alert for this driver
    const alert = mockAlerts.find(a => a.driverId === driverId);
    setSelectedAlertId(alert ? alert.id : null);
  };
  const handleSelectDeliveryDriver = (driverId: string, deliveryRef?: string) => {
    setSelectedDriverId(driverId);
    setHighlightedDriverIds(null);
    setScrollToRouteTs(Date.now());
    setSelectedDeliveryRef(deliveryRef || null);
    const alert = mockAlerts.find(a => a.driverId === driverId);
    setSelectedAlertId(alert ? alert.id : null);
  };
  const [alertFilterRequest, setAlertFilterRequest] = useState<{
    severity: 'all' | 'critical' | 'warning' | 'low';
    ts: number;
  } | null>(null);
  const handleFilterAlerts = (severity: 'critical' | 'warning' | 'low' | 'all') => {
    setAlertFilterRequest({
      severity,
      ts: Date.now()
    });
  };
  const handleContactDriver = (driverId: string) => {
    setCallingDriverId(driverId);
    const driver = mockDrivers.find(d => d.id === driverId);
    if (driver) {
      addLogEntry({
        type: 'contact',
        message: `Called ${driver.name}`,
        detail: `Outbound call initiated`,
        truckId: driver.truckId
      });
    }
  };
  const handleResolveAlert = (alertId: string) => {
    setResolvedAlertIds(prev => [...prev, alertId]);
    if (selectedAlertId === alertId) {
      setSelectedAlertId(null);
    }
    const alert = mockAlerts.find(a => a.id === alertId);
    const driver = alert ? mockDrivers.find(d => d.id === alert.driverId) : null;
    addLogEntry({
      type: 'alert-resolved',
      message: `Alert resolved: ${alert?.title || alertId}`,
      detail: alert?.description,
      truckId: driver?.truckId
    });
  };
  const handleDismissAlert = (alertId: string) => {
    setDismissedAlertIds(prev => [...prev, alertId]);
    if (selectedAlertId === alertId) {
      setSelectedAlertId(null);
    }
    const alert = mockAlerts.find(a => a.id === alertId);
    const driver = alert ? mockDrivers.find(d => d.id === alert.driverId) : null;
    addLogEntry({
      type: 'alert-dismissed',
      message: `Alert dismissed: ${alert?.title || alertId}`,
      detail: alert?.description,
      truckId: driver?.truckId
    });
  };
  // Compute effective drivers with updated statuses based on resolved/dismissed alerts and route overrides
  const effectiveDrivers = useMemo(() => {
    const hasCleared = resolvedAlertIds.length > 0 || dismissedAlertIds.length > 0;
    const hasRouteOverrides = Object.keys(routeOverrides).length > 0;
    const hasStatusOverrides = Object.keys(statusOverrides).length > 0;
    if (!hasCleared && !hasRouteOverrides && !hasStatusOverrides) {
      return mockDrivers;
    }
    const clearedAlertIds = new Set([...resolvedAlertIds, ...dismissedAlertIds]);
    return mockDrivers.map(driver => {
      // Apply route overrides
      const baseDriver = routeOverrides[driver.id] ? {
        ...driver,
        route: routeOverrides[driver.id]
      } : driver;
      // Apply explicit status overrides from rerouting
      if (statusOverrides[driver.id]) {
        const overriddenDriver = {
          ...baseDriver,
          status: statusOverrides[driver.id] as typeof baseDriver.status
        };
        return overriddenDriver;
      }
      if (baseDriver.status !== 'critical' && baseDriver.status !== 'warning') {
        return baseDriver;
      }
      // Find all alerts for this driver
      const driverAlertsList = mockAlerts.filter(a => a.driverId === baseDriver.id || a.affectedDriverIds?.includes(baseDriver.id));
      // Find remaining active alerts (not resolved/dismissed)
      const activeDriverAlerts = driverAlertsList.filter(a => !clearedAlertIds.has(a.id));
      // Determine new status based on remaining alerts
      const hasCritical = activeDriverAlerts.some(a => a.severity === 'critical');
      const hasWarning = activeDriverAlerts.some(a => a.severity === 'warning');
      let newStatus = baseDriver.status;
      if (!hasCritical && !hasWarning) {
        newStatus = 'normal';
      } else if (!hasCritical && hasWarning) {
        newStatus = 'warning';
      } else {
        newStatus = 'critical';
      }
      if (newStatus === baseDriver.status) return baseDriver;
      return {
        ...baseDriver,
        status: newStatus as typeof baseDriver.status
      };
    });
  }, [resolvedAlertIds, dismissedAlertIds, routeOverrides, statusOverrides]);
  const selectedDriver = effectiveDrivers.find(d => d.id === selectedDriverId) || null;
  const callingDriver = effectiveDrivers.find(d => d.id === callingDriverId) || null;
  const driverAlerts = selectedDriverId ? mockAlerts.filter(a => (a.driverId === selectedDriverId || a.affectedDriverIds?.includes(selectedDriverId)) && !resolvedAlertIds.includes(a.id) && !dismissedAlertIds.includes(a.id)) : [];
  return <div className="flex flex-col h-full w-full bg-slate-950 overflow-hidden" data-id="element-770">
      {/* Toast alerts disabled for demo simplicity */}

      <AnimatePresence data-id="element-771">
        {callingDriver && <CallCard driver={callingDriver} onEndCall={() => setCallingDriverId(null)} data-id="element-772" />}
      </AnimatePresence>

      <TopBar onFilterAlerts={handleFilterAlerts} data-id="element-773" />

      <SuccessToasts toasts={successToasts} onDismiss={id => setSuccessToasts(prev => prev.filter(t => t.id !== id))} data-id="element-774" />

      <div className="flex-1 overflow-hidden relative" data-id="element-775">
        {/* Map fills entire area */}
        <div className="absolute inset-0 z-10" data-id="element-776">
          <FleetMap drivers={effectiveDrivers} selectedDriverId={selectedDriverId} highlightedDriverIds={highlightedDriverIds} hoveredDriverIds={alertHoveredDriverIds || toastHoveredDriverIds} onSelectDriver={handleSelectDriver} onDeselect={() => {
          setSelectedDriverId(null);
          setSelectedAlertId(null);
          setHighlightedDriverIds(null);
        }} demoState={demoState} isRerouting={isRerouting} hoveredRouteId={hoveredRouteId} data-id="element-777" />
        </div>

        {/* Left panel overlays on top of map */}
        <div className="absolute left-0 top-0 bottom-0 z-20 pointer-events-none" data-id="element-778">
          <div className="pointer-events-auto h-full" data-id="element-779">
            <LeftPanel alerts={mockAlerts} drivers={effectiveDrivers} deliveries={mockDeliveries} triageItems={mockTriageItems} selectedAlertId={selectedAlertId} selectedDriverId={selectedDriverId} onSelectAlert={handleSelectAlert} onSelectDriver={handleSelectDriver} onSelectDeliveryDriver={handleSelectDeliveryDriver} requestedAlertFilter={alertFilterRequest} onContactDriver={handleContactDriver} onHoverAlert={setAlertHoveredDriverIds} resolvedAlertIds={resolvedAlertIds} dismissedAlertIds={dismissedAlertIds} onResolveAlert={handleResolveAlert} onDismissAlert={handleDismissAlert} logEntries={logEntries} data-id="element-780" />
          </div>
        </div>

        {/* Driver detail overlays on right */}
        <AnimatePresence data-id="element-781">
          {selectedDriver && <div className="absolute right-0 top-0 bottom-0 z-20" data-id="element-782">
              <DriverDetail driver={selectedDriver} alerts={driverAlerts} deliveries={mockDeliveries.filter(d => d.driverId === selectedDriverId)} scrollToRouteTs={scrollToRouteTs} selectedDeliveryRef={selectedDeliveryRef} onContactDriver={handleContactDriver} onResolveAlert={handleResolveAlert} onDismissAlert={handleDismissAlert} onUpdateRoute={handleUpdateRoute} onPreviewRoute={handlePreviewRoute} onCancelRoutePreview={handleCancelRoutePreview} onApplyReroute={handleApplyReroute} onRerouteToggle={setIsRerouting} onHoverRoute={setHoveredRouteId} data-id="element-783" />
            </div>}
        </AnimatePresence>
      </div>

      {showHidden && <>
          <DemoControls currentState={demoState} onStateChange={setDemoState} data-id="element-784" />
          <DesignNotes data-id="element-785" />
        </>}
    </div>;
}